/* ============================================
   Accesos — Admin: gestiona a qué empresas (cuentas)
   tiene acceso cada identidad (email) y con qué rol.
   Un email puede tener varias cuentas (cliente/partner).
   ============================================ */

import { useState, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import { PageHeader } from '@/components/ui'
import { IconPlus } from '@/components/icons/Icons'

type Rol = 'cliente' | 'partner'

interface Membership {
  cliente_id: number
  empresa: string | null
  role: Rol
  es_principal?: boolean
}

interface Usuario {
  id: number
  email: string
  must_change_password: boolean
  created_at: string
  memberships: Membership[]
}

interface ClienteOption {
  id: number
  empresa: string
}

const roleLabel: Record<string, string> = { cliente: 'Cliente', partner: 'Partner' }
const roleColor: Record<string, { bg: string; fg: string }> = {
  cliente: { bg: 'rgba(52,152,219,0.15)', fg: '#3498db' },
  partner: { bg: 'rgba(155,89,182,0.15)', fg: '#9b59b6' },
}

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let pw = ''
  for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)]
  return pw
}

export default function Accesos() {
  const api = useApi()

  const [search, setSearch] = useState('')

  // Gestionar accesos modal
  const [manageUser, setManageUser] = useState<Usuario | null>(null)
  const [addClienteId, setAddClienteId] = useState<number | ''>('')
  const [addRole, setAddRole] = useState<Rol>('cliente')
  const [savingMembership, setSavingMembership] = useState(false)

  // Nuevo usuario modal
  const [showCreate, setShowCreate] = useState(false)
  const [formEmail, setFormEmail] = useState('')
  const [formPassword, setFormPassword] = useState('')
  const [formClienteId, setFormClienteId] = useState<number | ''>('')
  const [formRole, setFormRole] = useState<Rol>('cliente')
  const [creating, setCreating] = useState(false)

  // Un minuto: es la pantalla donde se dan y se quitan accesos, y una lista de
  // cinco minutos podria mostrar activo a alguien a quien ya se lo revocaron.
  const { data: datos, isLoading: loading, refrescar: loadData } = useCachedData(
    ['accesos'],
    async () => {
      const [usuariosData, clientesData] = await Promise.all([
        api.get<Usuario[]>('/api/auth/usuarios'),
        api.get<ClienteOption[]>('/api/clientes'),
      ]).catch(() => {
        showToast('Error cargando accesos', 'error')
        return [[], []] as [Usuario[], ClienteOption[]]
      })
      return {
        usuarios: Array.isArray(usuariosData) ? usuariosData : [],
        clientes: Array.isArray(clientesData) ? clientesData : [],
      }
    },
    { staleTime: 60 * 1000 },
  )
  const usuarios = datos?.usuarios ?? []
  const clientes = datos?.clientes ?? []

  // Keep the manage modal in sync with fresh data after a reload
  useEffect(() => {
    if (!manageUser) return
    const fresh = usuarios.find(u => u.id === manageUser.id)
    if (fresh) setManageUser(fresh)
  }, [usuarios]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = usuarios.filter(u => {
    const q = search.toLowerCase()
    return u.email.toLowerCase().includes(q) || u.memberships.some(m => (m.empresa || '').toLowerCase().includes(q))
  })

  function openManage(u: Usuario) {
    setManageUser(u)
    setAddClienteId('')
    setAddRole('cliente')
  }

  async function addMembership() {
    if (!manageUser || !addClienteId) { showToast('Selecciona una empresa', 'error'); return }
    setSavingMembership(true)
    try {
      const res = await api.post<{ error?: string }>('/api/auth/memberships', {
        usuario_id: manageUser.id, cliente_id: addClienteId, role: addRole,
      })
      if (res?.error) { showToast(res.error, 'error'); return }
      showToast('Acceso agregado', 'success')
      setAddClienteId('')
      await loadData()
    } catch (err: any) {
      showToast(err.message || 'Error al agregar acceso', 'error')
    } finally {
      setSavingMembership(false)
    }
  }

  async function removeMembership(m: Membership) {
    if (!manageUser) return
    if (!confirm(`¿Quitar el acceso de ${manageUser.email} a "${m.empresa || m.cliente_id}"?`)) return
    try {
      await api.del(`/api/auth/memberships?usuario_id=${manageUser.id}&cliente_id=${m.cliente_id}`)
      showToast('Acceso quitado', 'success')
      await loadData()
    } catch {
      showToast('Error al quitar acceso', 'error')
    }
  }

  async function createUser() {
    if (!formEmail || !formPassword) { showToast('Email y contraseña son requeridos', 'error'); return }
    if (formPassword.length < 6) { showToast('La contraseña debe tener al menos 6 caracteres', 'error'); return }
    if (!formClienteId) { showToast('Selecciona una empresa', 'error'); return }
    setCreating(true)
    try {
      const res = await api.post<{ user?: { id: number }; error?: string }>('/api/auth/register', {
        email: formEmail, password: formPassword, role: formRole, cliente_id: formClienteId,
      })
      if (res?.error) { showToast(res.error, 'error'); return }
      showToast('Usuario creado', 'success')
      setShowCreate(false)
      setFormEmail(''); setFormPassword(''); setFormClienteId(''); setFormRole('cliente')
      await loadData()
    } catch (err: any) {
      showToast(err.message || 'Error al crear usuario', 'error')
    } finally {
      setCreating(false)
    }
  }

  // Empresas aún no asignadas al usuario que se gestiona
  const availableClientes = manageUser
    ? clientes.filter(c => !manageUser.memberships.some(m => m.cliente_id === c.id))
    : clientes

  if (loading) {
    return (
      <div className="page-enter" style={{ padding: '2rem' }}>
        <PageHeader breadcrumbs={[{ label: 'Clientes' }, { label: 'Accesos' }]} />
        <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
          <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando accesos...
        </div>
      </div>
    )
  }

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Clientes' }, { label: 'Accesos' }]}
        subtitle="Un mismo correo puede tener varias cuentas, cada una con su empresa y su rol."
        actions={
          <button className="btn btn-primary" onClick={() => { setFormEmail(''); setFormPassword(generatePassword()); setFormClienteId(''); setFormRole('cliente'); setShowCreate(true) }}>
            <IconPlus size={16} /> Nuevo usuario
          </button>
        }
      />

      {usuarios.length > 4 && (
        <div style={{ marginBottom: 16 }}>
          <input className="form-input" placeholder="Buscar por email o empresa..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 400 }} />
        </div>
      )}

      {usuarios.length === 0 ? (
        <div className="empty-state">
          <h3>Sin usuarios</h3>
          <p>Crea un usuario para darle acceso a una o varias empresas.</p>
          <button className="btn btn-primary" onClick={() => { setFormPassword(generatePassword()); setShowCreate(true) }}>Nuevo usuario</button>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead><tr><th>Email</th><th>Cuentas</th><th style={{ width: 90, textAlign: 'center' }}>N°</th><th style={{ width: 140 }}>Acciones</th></tr></thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>
                    {u.email}
                    {u.must_change_password && <span title="Debe cambiar contraseña" style={{ marginLeft: 6, display: 'inline-flex', verticalAlign: 'middle', color: 'var(--warning)' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg></span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {u.memberships.length === 0 && <span style={{ color: 'var(--text-muted)' }}>Sin cuentas</span>}
                      {u.memberships.map(m => (
                        <span key={m.cliente_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 12, fontSize: '0.78rem', background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                          {m.empresa || `#${m.cliente_id}`}
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0 6px', borderRadius: 8, background: roleColor[m.role]?.bg, color: roleColor[m.role]?.fg }}>{roleLabel[m.role] || m.role}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>{u.memberships.length}</td>
                  <td>
                    <button className="btn btn-sm btn-secondary" onClick={() => openManage(u)}>Gestionar accesos</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No se encontraron resultados</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Gestionar accesos */}
      {manageUser && (
        <Modal isOpen={!!manageUser} onClose={() => setManageUser(null)} title={`Accesos — ${manageUser.email}`}>
          <div>
            <h4 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Cuentas actuales</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
              {manageUser.memberships.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Este usuario aún no tiene cuentas. Agrega una abajo.</p>}
              {manageUser.memberships.map(m => (
                <div key={m.cliente_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.empresa || `Empresa #${m.cliente_id}`}</span>
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: roleColor[m.role]?.bg, color: roleColor[m.role]?.fg }}>{roleLabel[m.role] || m.role}</span>
                  </span>
                  <button className="btn btn-sm btn-danger" onClick={() => removeMembership(m)}>Quitar</button>
                </div>
              ))}
            </div>

            <h4 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Agregar una cuenta</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Empresa</label>
                <select className="form-select" value={addClienteId} onChange={e => setAddClienteId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Seleccionar empresa...</option>
                  {availableClientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: '0 0 140px' }}>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Rol</label>
                <select className="form-select" value={addRole} onChange={e => setAddRole(e.target.value as Rol)}>
                  <option value="cliente">Cliente</option>
                  <option value="partner">Partner</option>
                </select>
              </div>
              <button className="btn btn-primary" disabled={savingMembership || !addClienteId} onClick={addMembership} style={{ marginBottom: 2 }}>
                {savingMembership ? 'Agregando...' : 'Agregar'}
              </button>
            </div>
            {availableClientes.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 8 }}>El usuario ya tiene acceso a todas las empresas registradas.</p>}
          </div>
        </Modal>
      )}

      {/* Nuevo usuario */}
      {showCreate && (
        <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Nuevo usuario">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Empresa (primera cuenta)</label>
              <select className="form-select" value={formClienteId} onChange={e => setFormClienteId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Seleccionar empresa...</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
              </select>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Podrás agregarle más empresas después desde "Gestionar accesos".</span>
            </div>
            <div className="form-group">
              <label className="form-label">Rol en esta empresa</label>
              <select className="form-select" value={formRole} onChange={e => setFormRole(e.target.value as Rol)}>
                <option value="cliente">Cliente</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={formEmail} onChange={e => setFormEmail(e.target.value)} placeholder="usuario@empresa.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Contraseña inicial</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-input" value={formPassword} onChange={e => setFormPassword(e.target.value)} style={{ fontFamily: 'monospace' }} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFormPassword(generatePassword())} title="Generar contraseña"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg></button>
              </div>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Se le pedirá cambiarla al iniciar sesión.</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={creating} onClick={createUser}>{creating ? 'Creando...' : 'Crear usuario'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
